import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { ClipboardList, Factory, ImagePlus, IndianRupee, Plus, X, ChevronDown } from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";

const TARGET_TEAMS = [
    { value: "sales team", label: "Sales Team" },
    { value: "telemarketing", label: "Telemarketing" },
    { value: "admin", label: "Admin" },
];

const statusOptions = ["assigned", "in-review", "sent", "won", "lost"];
const normalizeTeam = (team = "") => {
    const value = String(team || "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
    if (value === "tele marketing" || value === "telemarketing") return "telemarketing";
    if (value === "administrator") return "admin";
    return value;
};
const isManufacturingTeam = (team = "") => normalizeTeam(team).includes("manufactur");
const isAudienceTeam = (team = "", role = "") => ["sales team", "telemarketing", "admin"].includes(normalizeTeam(team)) || normalizeTeam(role) === "admin";
const formatStatus = (status = "") => status.replace("-", " ");
const formatMoney = (amount) =>
    Number(amount || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const emptyForm = {
    title: "",
    customerName: "",
    quotationNo: "",
    amount: "",
    dueDate: "",
    details: "",
    proofPhoto: null,
    targetTeams: ["sales team"],
};

const Quotations = () => {
    const { employee } = useAuth();
    const [quotations, setQuotations] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [photoViewer, setPhotoViewer] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = useState(null);

    const canCreate = isManufacturingTeam(employee?.team);
    const canView = canCreate || isAudienceTeam(employee?.team, employee?.role);

    const stats = useMemo(() => ({
        total: quotations.length,
        active: quotations.filter((item) => !["won", "lost"].includes(item.status)).length,
        won: quotations.filter((item) => item.status === "won").length,
    }), [quotations]);

    const fetchQuotations = async () => {
        setLoading(true);
        try {
            const res = await api.get("/api/employee/quotations");
            setQuotations(res.data || []);
        } catch (error) {
            setMessage(error.response?.data?.message || "Unable to load quotations.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotations();
    }, []);

    const setField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const setQuotationPhoto = (file) => {
        if (!file) {
            setField("proofPhoto", null);
            return;
        }
        if (!file.type.startsWith("image/")) {
            setMessage("Please upload an image file for the quotation photo.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setForm((current) => ({
                ...current,
                proofPhoto: {
                    name: file.name,
                    data: reader.result,
                },
            }));
        };
        reader.readAsDataURL(file);
    };

    const toggleTarget = (team) => {
        setForm((current) => {
            const currentTargets = current.targetTeams || [];
            const nextTargets = currentTargets.includes(team)
                ? currentTargets.filter((item) => item !== team)
                : [...currentTargets, team];
            return { ...current, targetTeams: nextTargets };
        });
    };

    const createQuotation = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage("");
        try {
            await api.post("/api/employee/quotations", form);
            setForm(emptyForm);
            setIsModalOpen(false);
            setMessage("Quotation assigned successfully.");
            await fetchQuotations();
        } catch (error) {
            setMessage(error.response?.data?.message || "Unable to assign quotation.");
        } finally {
            setSaving(false);
        }
    };

    const updateStatus = async (id, status) => {
        setMessage("");
        try {
            const res = await api.patch(`/api/employee/quotations/${id}/status`, { status });
            setQuotations((current) => current.map((item) => item._id === id ? { ...item, status: res.data.status } : item));
        } catch (error) {
            setMessage(error.response?.data?.message || "Unable to update quotation.");
        }
    };

    if (!canView) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="quotations-premium-wrapper text-[var(--text-primary)]">
            <div className="max-w-[1320px] mx-auto px-4 space-y-6">

                {/* ================= INTEGRATED HEADER ================= */}
                <section className="quotations-integrated-header">
                    <div className="quotations-hero-content">
                        <span className="premium-eyebrow text-[#d97706]">
                            {canCreate ? "Manufacturing assignment" : "Assigned quotation queue"}
                        </span>
                        <h1>Quotations</h1>
                        <p className="mt-2 text-[var(--text-secondary)] font-medium">
                            Manage and track your sales quotations and deals
                        </p>
                        
                        {canCreate && (
                            <button type="button" onClick={() => setIsModalOpen(true)} className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d97706] to-[#b45309] px-6 text-sm font-black text-white hover:opacity-90 shadow-lg shadow-[#d97706]/20 transition-all hover:scale-105">
                                <Plus size={18} />
                                Assign Quotation
                            </button>
                        )}
                    </div>

                    <div className="quotations-stat-inline hidden md:flex">
                        <div className="quotations-stat-item">
                            <span className="quotations-stat-label">
                                <ClipboardList size={14} className="text-[#d97706]" /> Total
                            </span>
                            <span className="quotations-stat-value">{stats.total}</span>
                        </div>
                        <div className="quotations-stat-item">
                            <span className="quotations-stat-label">
                                <Factory size={14} className="text-[#ea580c]" /> Active
                            </span>
                            <span className="quotations-stat-value">{stats.active}</span>
                        </div>
                        <div className="quotations-stat-item border-none after:hidden">
                            <span className="quotations-stat-label">
                                <IndianRupee size={14} className="text-emerald-600" /> Won
                            </span>
                            <span className="quotations-stat-value">{stats.won}</span>
                        </div>
                    </div>
                </section>

                {message && (
                    <div className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-3 text-sm font-bold text-[var(--text-secondary)]">
                        {message}
                    </div>
                )}

                {canCreate && isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                        <div className="relative w-full max-w-3xl flex max-h-[90vh] flex-col rounded-2xl bg-[var(--bg-primary)] shadow-2xl overflow-hidden">
                            <div className="flex items-center justify-between border-b border-[var(--border-color)] p-5 shrink-0 bg-[var(--card-bg)]">
                                <h2 className="flex items-center gap-2 text-lg font-black">
                                    <Plus size={18} className="text-[var(--accent-primary)]" />
                                    Assign new quotation
                                </h2>
                                <button type="button" onClick={() => setIsModalOpen(false)} className="rounded-lg p-1 text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] hover:text-[var(--text-primary)]">
                                    <X size={20} />
                                </button>
                            </div>
                            
                            <div className="overflow-y-auto p-5">
                                <form onSubmit={createQuotation} className="flex flex-col">
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <Field label="Quotation title" value={form.title} onChange={(value) => setField("title", value)} required />
                                        <Field label="Customer name" value={form.customerName} onChange={(value) => setField("customerName", value)} />
                                        <Field label="Quotation number" value={form.quotationNo} onChange={(value) => setField("quotationNo", value)} />
                                        <Field label="Amount" type="number" value={form.amount} onChange={(value) => setField("amount", value)} />
                                        <Field label="Due date" type="date" value={form.dueDate} onChange={(value) => setField("dueDate", value)} />
                                        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3">
                                            <p className="mb-2 text-xs font-black uppercase text-[var(--text-secondary)]">Visible to</p>
                                            <div className="flex flex-wrap gap-2">
                                                {TARGET_TEAMS.map((team) => (
                                                    <button
                                                        key={team.value}
                                                        type="button"
                                                        onClick={() => toggleTarget(team.value)}
                                                        className={`rounded-full border px-3 py-2 text-xs font-black ${form.targetTeams.includes(team.value) ? "border-[var(--accent-primary)] bg-[var(--accent-primary)] text-white" : "border-[var(--border-color)]"}`}
                                                    >
                                                        {team.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <label className="mt-4 block rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3">
                                        <span className="text-xs font-black uppercase text-[var(--text-secondary)]">Quotation details</span>
                                        <textarea
                                            value={form.details}
                                            onChange={(event) => setField("details", event.target.value)}
                                            rows={4}
                                            className="mt-2 w-full resize-none bg-transparent text-sm font-semibold outline-none"
                                            placeholder="Items, quantity, requirement, follow-up notes..."
                                        />
                                    </label>
                                    <label className="quotation-photo-card mt-4">
                                        <span>Quotation photo</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(event) => setQuotationPhoto(event.target.files?.[0])}
                                            required={!form.proofPhoto?.data}
                                            className="quotation-photo-input"
                                        />
                                        <div className="quotation-photo-body">
                                            {form.proofPhoto?.data ? (
                                                <img src={form.proofPhoto.data} alt="Quotation preview" />
                                            ) : (
                                                <span className="quotation-photo-placeholder">
                                                    <ImagePlus size={18} />
                                                </span>
                                            )}
                                            <div>
                                                <strong>{form.proofPhoto?.name || "Upload quotation image"}</strong>
                                                <small>
                                                    Photo required before assigning
                                                </small>
                                            </div>
                                            <em>
                                                <ImagePlus size={14} />
                                                {form.proofPhoto?.data ? "Change photo" : "Choose photo"}
                                            </em>
                                        </div>
                                    </label>
                                    <button type="submit" disabled={saving} className="mt-5 inline-flex h-12 items-center justify-center rounded-xl bg-[var(--accent-primary)] px-5 text-sm font-black text-white hover:opacity-90 disabled:opacity-60">
                                        {saving ? "Assigning..." : "Assign quotation"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                <section className="mt-8">
                    <div className="flex items-center justify-between mb-6 px-2">
                        <h2 className="text-2xl font-bold tracking-tight">Quotation List</h2>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {loading ? (
                            <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-6 text-sm font-bold text-[var(--text-secondary)]">Loading quotations...</div>
                        ) : quotations.length ? quotations.map((quotation) => (
                            <QuotationListItem key={quotation._id} quotation={quotation} onClick={() => setSelectedQuotation(quotation)} />
                        )) : (
                            <div className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-6 text-center text-sm font-bold text-[var(--text-secondary)] lg:col-span-3">
                                No quotations found.
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {photoViewer?.proofPhoto?.data && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onMouseDown={() => setPhotoViewer(null)}>
                    <section className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-4 py-3">
                            <div className="min-w-0">
                                <p className="text-xs font-black uppercase tracking-widest text-[var(--accent-primary)]">Quotation photo</p>
                                <h2 className="truncate text-base font-black">{photoViewer.title || photoViewer.proofPhoto.name || "Quotation"}</h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPhotoViewer(null)}
                                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--accent-primary)] hover:text-white transition-colors"
                                aria-label="Close photo viewer"
                            >
                                <X size={17} />
                            </button>
                        </div>
                        <div className="max-h-[calc(90vh-72px)] overflow-auto bg-[var(--bg-primary)] p-4">
                            <img
                                src={photoViewer.proofPhoto.data}
                                alt={photoViewer.proofPhoto.name || photoViewer.title || "Quotation photo"}
                                className="mx-auto max-h-[calc(90vh-112px)] w-auto max-w-full rounded-xl object-contain"
                            />
                        </div>
                    </section>
                </div>
            )}

            {selectedQuotation && (
                <QuotationDetailsModal
                    quotation={selectedQuotation}
                    onClose={() => setSelectedQuotation(null)}
                    onStatus={(status) => updateStatus(selectedQuotation._id, status)}
                    onViewPhoto={setPhotoViewer}
                />
            )}
        </div>
    );
};

const Field = ({ label, value, onChange, type = "text", required = false }) => (
    <label className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
        <span className="text-xs font-black uppercase text-[var(--text-secondary)]">{label}</span>
        <input
            type={type}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            required={required}
            className="mt-2 w-full bg-transparent text-sm font-bold outline-none"
        />
    </label>
);

const QuotationListItem = ({ quotation, onClick }) => (
    <button type="button" onClick={onClick} className="quotation-list-item">
        <div className="flex flex-col gap-1 min-w-0 pr-4">
            <h3 className="truncate text-base font-black text-[var(--text-primary)]">{quotation.title}</h3>
            <p className="truncate text-xs font-bold text-[var(--text-secondary)]">
                {quotation.customerName || "No customer"}
            </p>
        </div>
        <span className="rounded-full bg-[#d97706]/10 px-2.5 py-1 text-[10px] font-black uppercase text-[#d97706] shrink-0 border border-[#d97706]/20">
            {formatStatus(quotation.status)}
        </span>
    </button>
);

const QuotationDetailsModal = ({ quotation, onClose, onStatus, onViewPhoto }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4 backdrop-blur-sm" onMouseDown={onClose}>
        <div className="quotation-details-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="lux-modal-header flex items-center justify-between shrink-0">
                <div className="min-w-0 pr-4">
                    <h2 className="truncate text-xl font-black text-[var(--text-primary)]">{quotation.title}</h2>
                    <p className="truncate text-xs font-bold text-[var(--text-secondary)] mt-1 opacity-70">
                        {quotation.customerName || "No customer"} {quotation.quotationNo ? `- ${quotation.quotationNo}` : ""}
                    </p>
                </div>
                <button type="button" onClick={onClose} className="rounded-none p-2 text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] transition-all">
                    <X size={20} />
                </button>
            </div>
            
            <div className="overflow-y-auto p-6 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] mb-2 tracking-widest opacity-70">Current Status</p>
                        <CustomDropdown value={quotation.status} options={statusOptions} onChange={onStatus} />
                    </div>
                    {quotation.proofPhoto?.data && (
                        <button type="button" onClick={() => onViewPhoto(quotation)} className="lux-photo-btn shrink-0">
                            <img src={quotation.proofPhoto.data} alt="Quotation" />
                            <span className="pr-2 tracking-wide uppercase text-[10px]">View photo</span>
                        </button>
                    )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="lux-info-card">
                        <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] tracking-widest opacity-70">Amount</p>
                        <p className="mt-2 text-xl font-black">{formatMoney(quotation.amount) || "-"}</p>
                    </div>
                    <div className="lux-info-card">
                        <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] tracking-widest opacity-70">Due date</p>
                        <p className="mt-2 text-xl font-black">{quotation.dueDate ? new Date(quotation.dueDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'long', year: 'numeric' }) : "-"}</p>
                    </div>
                    <div className="col-span-2 sm:col-span-2 lux-info-card">
                        <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] tracking-widest opacity-70">Visible to</p>
                        <p className="mt-2 text-base font-bold">{(quotation.targetTeams || []).map((team) => TARGET_TEAMS.find((item) => item.value === normalizeTeam(team))?.label || team).join(", ") || "-"}</p>
                    </div>
                </div>

                {quotation.details && (
                    <div>
                        <p className="text-[10px] font-black uppercase text-[var(--text-secondary)] mb-3 tracking-widest opacity-70">Details</p>
                        <p className="lux-details-box whitespace-pre-wrap text-sm font-semibold leading-relaxed text-[var(--text-secondary)]">
                            {quotation.details}
                        </p>
                    </div>
                )}
            </div>
        </div>
    </div>
);

const CustomDropdown = ({ value, options, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClick = () => setIsOpen(false);
        if (isOpen) window.addEventListener("click", handleClick);
        return () => window.removeEventListener("click", handleClick);
    }, [isOpen]);

    return (
        <div className="relative w-full sm:w-auto shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex h-9 w-full sm:w-[140px] items-center justify-between gap-2 rounded-none border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold tracking-wide capitalize transition-all hover:border-[var(--accent-primary)] hover:shadow-[0_0_0_3px_var(--accent-primary)]/10 focus:outline-none ${isOpen ? "border-[var(--accent-primary)] ring-1 ring-[var(--accent-primary)]" : ""}`}
            >
                <span className="truncate">{formatStatus(value)}</span>
                <ChevronDown size={14} className={`text-[var(--text-secondary)] transition-transform duration-200 ${isOpen ? "rotate-180 text-[var(--accent-primary)]" : ""}`} />
            </button>
            
            {isOpen && (
                <div className="absolute right-0 top-full z-10 mt-2 w-full sm:w-[140px] animate-in fade-in slide-in-from-top-2 rounded-none border border-[var(--border-color)] bg-[var(--card-bg)] p-1 shadow-xl">
                    <ul className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
                        {options.map((option) => (
                            <li key={option}>
                                <button
                                    type="button"
                                    onClick={() => { onChange(option); setIsOpen(false); }}
                                    className={`w-full rounded-none px-3 py-2 text-left text-sm font-medium capitalize transition-colors ${value === option ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]" : "text-[var(--text-primary)] hover:bg-[var(--bg-primary)]"}`}
                                >
                                    {formatStatus(option)}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Quotations;
